import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import LeadCTA from "@/components/LeadCTA";
import WaitlistDialog from "@/components/WaitlistDialog";
import {
  ChartBar, Crosshair, Key, ArrowRight, Infinity as InfinityIcon,
} from "@phosphor-icons/react";

const TOOLS = [
  { slug: "catalog-analyzer", no: "01", title: "Catalog Analyzer", desc: "Paste any Amazon URL / ASIN → scores, recommendations, keywords, competitor table.", icon: ChartBar, chip: "bg-persimmon text-white", accent: "text-persimmon" },
  { slug: "listing-optimizer", no: "02", title: "Listing Optimizer", desc: "Rewrite title and 5 bullets. Rationale included for every change.", icon: Crosshair, chip: "bg-cobalt text-white", accent: "text-cobalt" },
  { slug: "keyword-generator", no: "03", title: "Keyword Generator", desc: "Seed term → 15–20 ranked keywords with relevance and competition signals.", icon: Key, chip: "bg-butter text-ink", accent: "text-ink" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);

  useEffect(() => { api.get("/tools/usage").then((r) => setUsage(r.data)).catch(() => {}); }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
      <div className="grid md:grid-cols-12 gap-8 items-end">
        <div className="md:col-span-8">
          <div className="label-mono">Workspace</div>
          <h1 className="mt-3 font-display font-black text-5xl md:text-6xl tracking-tightest text-ink leading-[1]">
            Hey {user?.name?.split(" ")[0] || "seller"}.
            <br /><span className="text-ink/40">What are we fixing today?</span>
          </h1>
          <p className="mt-5 text-ink/65 max-w-xl leading-relaxed">
            {usage?.is_admin
              ? "Admin · unlimited runs across all analysts."
              : `${usage?.limit ?? 5} free runs per analyst, per day. Resets at midnight (Asia/Kolkata).`}
          </p>
        </div>
        <div className="md:col-span-4 flex md:justify-end gap-2 flex-wrap">
          <WaitlistDialog />
          <LeadCTA sourcePage="dashboard" />
        </div>
      </div>

      {/* Tool tiles — dense grid, 1px rule */}
      <div className="mt-14 grid md:grid-cols-3 border border-rule bg-white">
        {TOOLS.map((t, i) => {
          const u = usage?.tools?.[t.slug];
          const remaining = u?.remaining ?? 5;
          const limit = usage?.limit ?? 5;
          const used = u?.used ?? 0;
          const isAdmin = usage?.is_admin;
          return (
            <Link
              key={t.slug}
              to={`/tools/${t.slug}`}
              data-testid={`tool-card-${t.slug}`}
              className={`group p-8 md:p-10 hover:bg-paper transition-colors flex flex-col ${i < 2 ? "md:border-r border-rule" : ""}`}
            >
              <div className="flex items-start justify-between">
                <div className={`w-12 h-12 ${t.chip} grid place-items-center group-hover:scale-105 transition-transform`}>
                  <t.icon weight="bold" className="w-6 h-6" />
                </div>
                <div className="label-mono text-right">
                  <div>№ {t.no}</div>
                  <div className={`mt-1 ticker ${t.accent}`}>
                    {isAdmin ? "∞ unlimited" : `${remaining}/${limit} left`}
                  </div>
                </div>
              </div>
              <div className="mt-12 font-display text-3xl font-bold tracking-tightest text-ink leading-tight">{t.title}</div>
              <p className="mt-3 text-ink/65 text-sm leading-relaxed flex-1">{t.desc}</p>
              <div className="mt-8 flex items-center justify-between border-t border-rule pt-5">
                <span className="label-mono">Used today · {used}</span>
                <span className={`font-medium inline-flex items-center group-hover:translate-x-0.5 transition-transform ${t.accent}`}>
                  Open <ArrowRight className="w-4 h-4 ml-1.5" weight="bold" />
                </span>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="mt-12 border border-ink bg-butter p-8 md:p-10 grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-8">
          <div className="label-mono">Recommendation</div>
          <div className="mt-3 font-display font-bold text-2xl tracking-tightest text-ink">Start with the Catalog Analyzer.</div>
          <p className="mt-2 text-ink/75 text-sm leading-relaxed">
            It pulls scores, recommendations, keywords, and competitors in one shot — and exports a PDF you can hand to a VA.
          </p>
        </div>
        <div className="md:col-span-4 md:text-right">
          <Button asChild data-testid="dashboard-start-btn">
            <Link to="/tools/catalog-analyzer">
              Open Catalog Analyzer <ArrowRight className="w-4 h-4 ml-2" weight="bold" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
